'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { useDeviceType } from '@/hooks/useIsMobile';
import { useSyncViewMode } from '@/hooks/useSyncViewMode';
import { useThemeColor } from '@/hooks/useThemeColor';
import { ENTRY_VIEW_ROUTES, PLAIN_VIEW_ROUTES } from '@/constants/constants';
import { ViewMode } from '@/types/features';
import { matchesAny } from '@/utils/helpers/routeHelper';
import MouseStalker from '@/components/MouseStalker';
import Toast from '@/components/Toast';
import MobileFooter from '@/components/layout/base/unit/MobileFooter';
import MobileHeader from '@/components/layout/base/unit/MobileHeader';
import PcFooter from '@/components/layout/base/unit/PcFooter';
import PcHeader from '@/components/layout/base/unit/PcHeader';
import ModalAlertWrapper from '@/components/modal/ModalAlertWrapper';
import ModalConfirmWrapper from '@/components/modal/ModalConfirmWrapper';
import ModalSlideWrapper from '@/components/modal/ModalSlideWrapper';
import ModalWrapper from '@/components/modal/ModalWrapper';

interface BaseLayoutProps {
    children: React.ReactNode;
}

export default function BaseLayout({ children }: BaseLayoutProps) {
    const pathname = usePathname();
    const { isMobile, isTablet } = useDeviceType();
    const { scheme } = useThemeColor();

    // useSyncViewMode はストア更新用。BaseLayout内のレンダリングはちらつき防止のためuseMemoで導出。
    useSyncViewMode();

    const viewMode = useMemo<ViewMode>(() => {
        if (matchesAny(ENTRY_VIEW_ROUTES, pathname)) return 'entry';
        if (matchesAny(PLAIN_VIEW_ROUTES, pathname)) return 'plain';
        return 'normal';
    }, [pathname]);

    const bgClass = scheme?.bg ?? '';
    const bgTextClass = scheme?.bgText ?? '';

    return (
        <>
            <MouseStalker />
            <ModalWrapper />
            <ModalSlideWrapper />
            <ModalAlertWrapper />
            <ModalConfirmWrapper />
            <Toast />

            {viewMode === 'entry' ? (
                <>{children}</>
            ) : viewMode === 'plain' ? (
                <div className={`${bgClass} ${bgTextClass} bg-svg`}>{children}</div>
            ) : isMobile ? (
                <div className={`h-dvh flex flex-col ${bgClass} ${bgTextClass}`}>
                    <MobileHeader />
                    <main
                        id="main-scroll-area"
                        className="flex-1 overflow-y-scroll overflow-x-hidden p-3"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                        {children}
                    </main>
                    <MobileFooter />
                </div>
            ) : (
                <div className={`flex flex-col h-screen w-full overflow-hidden ${bgClass} ${bgTextClass}`}>
                    <PcHeader />
                    <main
                        className="flex-grow overflow-y-auto overflow-x-hidden pt-3 px-3 mt-16"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                        {children}
                    </main>
                    <PcFooter />
                </div>
            )}
        </>
    );
}
